import Dialog from '@mui/material/Dialog';
import React from 'react';
import { AdminButton } from './AdminButton';

export type ConfirmDialogCloseReason =
  | 'cancel'
  | 'confirmed'
  | 'escapeKeyDown'
  | 'backdropClick';

export interface ConfirmDialogProps {
  open: boolean;
  title: React.ReactNode;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  closeOnConfirm?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: (reason: ConfirmDialogCloseReason) => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmDisabled = false,
  closeOnConfirm = true,
  onConfirm,
  onClose,
}) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  const confirm = () => {
    if (confirmDisabled) {
      return;
    }

    const result = onConfirm();
    if (!closeOnConfirm) {
      return;
    }
    if (result instanceof Promise) {
      void result.then(() => onClose('confirmed'));
    } else {
      onClose('confirmed');
    }
  };

  return (
    <Dialog
      open={open}
      className="pipeex-admin-portal admin-confirm-dialog-root"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClose={(_, reason) => onClose(reason)}
      slotProps={{
        backdrop: { className: 'pipeex-admin-portal admin-confirm-dialog-backdrop' },
        paper: { className: 'pipeex-admin-portal admin-confirm-dialog' },
      }}
    >
      <div id={titleId} className="admin-confirm-dialog__title">{title}</div>
      <div id={descriptionId} className="admin-confirm-dialog__description">{description}</div>
      <div className="admin-confirm-dialog__actions">
        <AdminButton variant="ghost" onClick={() => onClose('cancel')}>{cancelLabel}</AdminButton>
        <AdminButton variant="danger" disabled={confirmDisabled} onClick={confirm}>
          {confirmLabel}
        </AdminButton>
      </div>
    </Dialog>
  );
};
